/**
 * ExistingSolarConfiguration - Configure and simulate existing solar assets
 * 
 * Allows users to model existing solar systems that reduce baseline load.
 */

import React, { useState } from 'react';
import {
    Box,
    TextField,
    Button,
    Typography,
    Paper,
    Alert,
    CircularProgress,
    Grid,
    FormControlLabel,
    Checkbox,
} from '@mui/material';
import { SolarPower } from '@mui/icons-material';
import { API_BASE_URL } from '@vortx/shared-ui';

export function ExistingSolarConfiguration({
    latitude,
    longitude,
    existingSolarParams = {},
    existingSolarProfile = [],
    baseLoadIsNetOfSolar = false,
    existingSolarCanExport = false,
    onSimulate,
    onClear,
    onBaseLoadModeChange,
    onExportModeChange,
}) {
    const [params, setParams] = useState({
        system_capacity: existingSolarParams.system_capacity || '',
        tilt: existingSolarParams.tilt || 20,
        azimuth: existingSolarParams.azimuth || 180,
        losses: existingSolarParams.losses || 14.0,
        array_type: existingSolarParams.array_type || 1,
        module_type: existingSolarParams.module_type || 0,
    });

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    const handleParamChange = (field) => (event) => {
        setParams({ ...params, [field]: event.target.value });
        setError(null);
        setSuccess(null);
    };

    const handleSimulate = async () => {
        // Validation
        if (!params.system_capacity || parseFloat(params.system_capacity) <= 0) {
            setError('System capacity must be a positive number');
            return;
        }

        if (!latitude || !longitude) {
            setError('Site coordinates are required. Please configure site location first.');
            return;
        }

        setLoading(true);
        setError(null);
        setSuccess(null);

        try {
            const response = await fetch(`${API_BASE_URL}/api/site_dna/v1/nrel/pvwatts`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Frontend-Source': 'react-frontend',
                },
                body: JSON.stringify({
                    latitude,
                    longitude,
                    system_capacity: parseFloat(params.system_capacity),
                    tilt: parseFloat(params.tilt),
                    azimuth: parseFloat(params.azimuth),
                    losses: parseFloat(params.losses),
                    array_type: parseInt(params.array_type),
                    module_type: parseInt(params.module_type),
                }),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'PVWatts simulation failed');
            }

            const data = await response.json();

            if (!data.outputs || !data.outputs.ac || data.outputs.ac.length !== 8760) {
                throw new Error('Invalid PVWatts response');
            }

            // Convert PVWatts AC output from Watts to kilowatts
            // PVWatts API returns hourly AC output in Watts, but our load profiles are in kW
            const acKw = data.outputs.ac.map(watts => watts / 1000);

            // Call parent callback with results (using kW values)
            onSimulate(params, acKw, data.outputs);

            setSuccess(
                `Solar simulation complete! Annual Production: ${data.outputs.ac_annual.toLocaleString(undefined, { maximumFractionDigits: 0 })} kWh`
            );
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleClear = () => {
        setParams({
            system_capacity: '',
            tilt: 20,
            azimuth: 180,
            losses: 14.0,
            array_type: 1,
            module_type: 0,
        });
        setError(null);
        setSuccess(null);
        onClear();
    };

    const hasExistingSolar = existingSolarProfile.length === 8760;
    const annualProduction = hasExistingSolar
        ? existingSolarProfile.reduce((sum, val) => sum + val, 0)
        : 0;

    return (
        <Paper sx={{ p: 2, mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <SolarPower sx={{ mr: 1, color: 'warning.main' }} />
                <Typography variant="h6">Existing Solar</Typography>
            </Box>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Model existing solar assets. Production will be subtracted from your facility load to
                create the net load for optimization.
            </Typography>

            <FormControlLabel
                control={
                    <Checkbox
                        checked={baseLoadIsNetOfSolar}
                        onChange={(e) => onBaseLoadModeChange?.(e.target.checked)}
                        color="primary"
                    />
                }
                label={
                    <Typography variant="body2">
                        Base load profile is already net of existing solar
                        <Typography component="span" variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            (Check if your uploaded load profile already has solar production subtracted)
                        </Typography>
                    </Typography>
                }
                sx={{ mb: 2 }}
            />

            <FormControlLabel
                control={
                    <Checkbox
                        checked={existingSolarCanExport}
                        onChange={(e) => onExportModeChange?.(e.target.checked)}
                        color="primary"
                    />
                }
                label={
                    <Typography variant="body2">
                        System can export excess solar to grid
                        <Typography component="span" variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            (Check if solar production can exceed facility load and export to grid)
                        </Typography>
                    </Typography>
                }
                sx={{ mb: 2 }}
            />

            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <TextField
                        fullWidth
                        label="System Capacity"
                        type="number"
                        value={params.system_capacity}
                        onChange={handleParamChange('system_capacity')}
                        InputProps={{ inputProps: { min: 0, step: 0.1 } }}
                        helperText="kW DC"
                    />
                </Grid>
                <Grid item xs={4}>
                    <TextField
                        fullWidth
                        label="Tilt"
                        type="number"
                        value={params.tilt}
                        onChange={handleParamChange('tilt')}
                        InputProps={{ inputProps: { min: 0, max: 90, step: 1 } }}
                        helperText="degrees"
                    />
                </Grid>
                <Grid item xs={4}>
                    <TextField
                        fullWidth
                        label="Azimuth"
                        type="number"
                        value={params.azimuth}
                        onChange={handleParamChange('azimuth')}
                        InputProps={{ inputProps: { min: 0, max: 360, step: 1 } }}
                        helperText="180=South"
                    />
                </Grid>
                <Grid item xs={4}>
                    <TextField
                        fullWidth
                        label="Losses"
                        type="number"
                        value={params.losses}
                        onChange={handleParamChange('losses')}
                        InputProps={{ inputProps: { min: 0, max: 99, step: 0.1 } }}
                        helperText="percent"
                    />
                </Grid>
            </Grid>

            <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                <Button
                    variant="contained"
                    onClick={handleSimulate}
                    disabled={loading || !params.system_capacity}
                    startIcon={loading ? <CircularProgress size={20} /> : <SolarPower />}
                >
                    {loading ? 'Simulating...' : 'Simulate & Apply'}
                </Button>
                {hasExistingSolar && (
                    <Button variant="outlined" onClick={handleClear}>
                        Clear Existing Solar
                    </Button>
                )}
            </Box>

            {error && (
                <Alert severity="error" sx={{ mt: 2 }}>
                    {error}
                </Alert>
            )}

            {success && (
                <Alert severity="success" sx={{ mt: 2 }}>
                    {success}
                </Alert>
            )}

            {hasExistingSolar && (
                <Box sx={{ mt: 2, p: 2, bgcolor: 'success.light', borderRadius: 1 }}>
                    <Typography variant="subtitle2" color="success.contrastText">
                        Active Existing Solar System
                    </Typography>
                    <Typography variant="body2" color="success.contrastText">
                        {params.system_capacity} kW DC | Annual Production:{' '}
                        {annualProduction.toLocaleString(undefined, { maximumFractionDigits: 0 })} kWh
                    </Typography>
                </Box>
            )}
        </Paper>
    );
}

export default ExistingSolarConfiguration;
